import { useCallback, useState } from "react"
import { useLocalStorage } from "react-use"
import { getLocalStorageItem } from "~/services/Utils"
import {
  CONSENT_VERSION,
  CookieConsentChoice,
  CookieConsentPreferences,
  CookieConsentRecord,
  CookieConsentSnapshot,
  COOKIE_CATEGORIES,
  COOKIE_CONSENT_CHOICES,
  COOKIE_CONSENT_INITIAL_PREFERENCES,
  COOKIE_CONSENT_PRIVACY_SIGNAL_PREFERENCES,
  DEFAULT_STORAGE_KEY,
} from "../CookieConsentTypes"

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

export const isValidConsentRecord = (
  value: unknown
): value is CookieConsentRecord => {
  if (!isPlainObject(value)) {
    return false
  }

  const { version, choice, preferences, updatedAt } = value

  if (version !== CONSENT_VERSION) {
    return false
  }

  if (!COOKIE_CONSENT_CHOICES.some(knownChoice => knownChoice === choice)) {
    return false
  }

  if (!isPlainObject(preferences)) {
    return false
  }

  const hasAllCategories = COOKIE_CATEGORIES.every(
    category => typeof preferences[category] === "boolean"
  )

  const isValid = hasAllCategories && typeof updatedAt === "string"

  return isValid
}

// globalPrivacyControl is not in the TypeScript DOM types yet
type NavigatorWithGPC = Navigator & { globalPrivacyControl?: boolean }

export const hasGlobalPrivacyControl = () => {
  if (typeof navigator === "undefined") {
    return false
  }

  const { globalPrivacyControl } = navigator as NavigatorWithGPC

  return globalPrivacyControl === true
}

// Global Privacy Control opts out of everything optional, unless the visitor
// made their choice while the signal was already on
const applyGlobalPrivacyControl = (record?: CookieConsentRecord) => {
  if (!record || !hasGlobalPrivacyControl() || record.globalPrivacyControl) {
    return record
  }

  return { ...record, preferences: COOKIE_CONSENT_PRIVACY_SIGNAL_PREFERENCES }
}

// read outside React so the saga can send it with the order
export const getCookieConsentSnapshot = (): CookieConsentSnapshot => {
  // no default value, the helper would write it to storage
  const storedValue = getLocalStorageItem(DEFAULT_STORAGE_KEY)
  const record = applyGlobalPrivacyControl(
    isValidConsentRecord(storedValue) ? storedValue : undefined
  )

  // matches the default the inline script sends to GTM
  const unsavedPreferences = hasGlobalPrivacyControl()
    ? COOKIE_CONSENT_PRIVACY_SIGNAL_PREFERENCES
    : COOKIE_CONSENT_INITIAL_PREFERENCES

  return {
    choice: record?.choice ?? null,
    preferences: record?.preferences ?? unsavedPreferences,
    updatedAt: record?.updatedAt ?? null,
    version: CONSENT_VERSION,
    globalPrivacyControl: hasGlobalPrivacyControl(),
  }
}

export const useCookieConsentStorage = () => {
  const [storedValue, setStoredValue] =
    useLocalStorage<unknown>(DEFAULT_STORAGE_KEY)

  const [memoryRecord, setMemoryRecord] = useState<CookieConsentRecord>()

  const savedRecord = isValidConsentRecord(storedValue)
    ? storedValue
    : memoryRecord

  const record = applyGlobalPrivacyControl(savedRecord)

  const save = useCallback(
    (
      choice: CookieConsentChoice,
      preferences: CookieConsentPreferences
    ): CookieConsentRecord => {
      const nextRecord: CookieConsentRecord = {
        version: CONSENT_VERSION,
        choice,
        // necessary cookies can never be opted out of
        preferences: { ...preferences, necessary: true },
        updatedAt: new Date().toISOString(),
        globalPrivacyControl: hasGlobalPrivacyControl(),
      }

      setStoredValue(nextRecord)
      setMemoryRecord(nextRecord)

      return nextRecord
    },
    [setStoredValue]
  )

  return { record, save }
}
